import React from 'react';
import { useVerifyServer } from '../../hooks/useVerifyServer';
import { api } from '../../services/api';
import { LangContext } from '../../contexts/LangContext';
import './Journey.css';

const CLAIM_API = 'payment/loyalty-path/claim-reward';

function assignPhases(n) {
  return Array.from({ length: n }, (_, i) => {
    if (i === 0) return 1;
    if (i === n - 1) return 5;
    return n <= 5 ? i + 1 : ((i - 1) % 4) + 1;
  });
}

function chestImg(phase, opened, ready, chestType) {
  const suffix = opened ? '-opened' : ready ? '-ready' : '';
  const type = chestType != null ? String(chestType) : String(phase);
  return `img/loyalty/chests/chest${type}${suffix}@2x.png`;
}

function formatDate(dateStr) {
  const d = new Date(dateStr);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}/${d.getFullYear()}`;
}

function fmtN(n) {
  return n.toLocaleString('pt-BR');
}

export default function Journey({ edition, claimedGoalIds, claimedGoals = [], label, server, token, username, cartTotalSpend = 0, onClaimSuccess }) {
  const serverMap = useVerifyServer(server);
  const { lang, t } = React.useContext(LangContext);
  const lp = t.component.loyaltyPath;

  const [activePopup, setActivePopup]   = React.useState(null);
  const [popupFlipped, setPopupFlipped] = React.useState(false);
  const [itemNames, setItemNames]       = React.useState({});
  const [localClaimed, setLocalClaimed] = React.useState([]);
  const [claiming, setClaiming]         = React.useState(null);
  const [loadingDots, setLoadingDots]   = React.useState(1);
  const [claimModal, setClaimModal]     = React.useState(null);
  const [barTooltipPos, setBarTooltipPos] = React.useState(null);
  const barWrapperRef                   = React.useRef(null);
  const [timeLeft, setTimeLeft]         = React.useState('');
  const [isOverflowing, setIsOverflowing] = React.useState(false);
  const popupRef           = React.useRef(null);
  const scrollContainerRef = React.useRef(null);
  const scrollInnerRef     = React.useRef(null);
  const dragState          = React.useRef({ active: false, startX: 0, startScrollLeft: 0 });
  const chestsLengthRef    = React.useRef(0);

  React.useEffect(() => {
    const el = scrollInnerRef.current;
    if (!el) return;
    // Compara espaço necessário vs espaço disponível — sem depender do min-width do filho
    const check = () => setIsOverflowing(chestsLengthRef.current * 200 > el.clientWidth);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  React.useEffect(() => {
    const el = scrollInnerRef.current;
    if (!el) return;

    const onMouseDown = (e) => {
    if (el.scrollWidth <= el.clientWidth) return;      
    dragState.current = { active: true, startX: e.clientX, startScrollLeft: el.scrollLeft };
      el.style.cursor = 'grabbing';
      setActivePopup(null);
    };
    const onMouseMove = (e) => {
      if (!dragState.current.active) return;
      el.scrollLeft = dragState.current.startScrollLeft - (e.clientX - dragState.current.startX);
    };
    const onMouseUp = () => {
      dragState.current.active = false;
      el.style.cursor = '';
    };

    el.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mouseup', onMouseUp);
    return () => {
      el.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mouseup', onMouseUp);
    };
  }, []);

  const accountName = username || '';

  React.useEffect(() => {
    if (!edition) return;
    const allIds = [...new Set(edition.goals.flatMap(g => g.items.map(i => i.itemId)))];
    allIds.forEach(itemId => {
      // A rota passou a exigir token de sessao. Como nao ha conta no caminho, username e
      // token vao pela query -- os dois ja chegam aqui como prop e sao os mesmos usados no
      // claim-reward abaixo.
      api.get(`payment/global_prod/items/${itemId}`, { params: { username: accountName, token } })
        .then(res => setItemNames(prev => ({ ...prev, [itemId]: res.data?.item ?? [] })))
        .catch(() => {});
    });
  }, [edition, accountName, token]);

  React.useEffect(() => {
    if (!edition?.endDate) return;
    const update = () => {
      const diff = new Date(edition.endDate) - new Date();
      if (diff <= 0) { setTimeLeft('00:00:00'); return; }
      const days  = Math.floor(diff / 86400000);
      const hours = Math.floor((diff % 86400000) / 3600000);
      const mins  = Math.floor((diff % 3600000) / 60000);
      const secs  = Math.floor((diff % 60000) / 1000);
      const pad   = n => String(n).padStart(2, '0');
      setTimeLeft(days > 0
        ? `${days} dias ${pad(hours)}:${pad(mins)}:${pad(secs)}`
        : `${pad(hours)}:${pad(mins)}:${pad(secs)}`
      );
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [edition?.endDate]);

  React.useEffect(() => {
    if (claiming === null) { setLoadingDots(1); return; }
    const id = setInterval(() => setLoadingDots(d => d === 3 ? 1 : d + 1), 400);
    return () => clearInterval(id);
  }, [claiming]);

  // Limpa contagem local quando o servidor confirma os dados atualizados,
  // evitando dupla contagem entre serverTimesClaimed e localClaimed.
  React.useEffect(() => {
    setLocalClaimed([]);
  }, [claimedGoals]);

  React.useLayoutEffect(() => {
    if (!activePopup || !popupRef.current) { setPopupFlipped(false); return; }
    const rect = popupRef.current.getBoundingClientRect();
    setPopupFlipped(rect.right > window.innerWidth - 8);
  }, [activePopup]);

  if (!edition) return null;

  const { goals, startDate, endDate, playerPoints, pointsBRL, pointsUSD,
          consumedPoints: backendConsumedPoints,
          carryoverPoints = 0, carryoverPct = 0 } = edition;
  const pointsRate    = (lang === 'USD' ? pointsUSD : pointsBRL) ?? 0;
  const pendingPoints = cartTotalSpend * pointsRate;
  const phases     = assignPhases(goals.length);
  const isMonthly  = edition.type === 'monthly';

  // claimedGoals (com times_claimed) é o formato v2; claimedGoalIds é o fallback
  // enquanto o backend antigo estiver no ar
  const serverTimesClaimed = (goalId) => {
    const found = claimedGoals?.find(g => g.goalId === goalId);
    if (found) return found.timesClaimed;
    return claimedGoalIds.includes(goalId) ? 1 : 0;
  };
  const getTimesClaimed = (goalId) =>
    serverTimesClaimed(goalId) + localClaimed.filter(id => id === goalId).length;

  const chests = goals.map((goal, i) => ({
    goalId:       goal.goalId,
    phase:        phases[i],
    chestType:    goal.chestType,
    timesClaimed: getTimesClaimed(goal.goalId),
    opened:       getTimesClaimed(goal.goalId) > 0,
    pointsNeeded: goal.pointsNeeded,
    items:        goal.items,
  }));
  chestsLengthRef.current = chests.length;

  const maxPoints    = goals.length > 0 ? goals[goals.length - 1].pointsNeeded : 0;
  // Modelo de consumo (apenas monthly): step = última meta − penúltima;
  // consumido = penúltima + n×step quando a última foi resgatada n vezes,
  // senão o maior threshold já resgatado
  const penPoints  = goals.length > 1 ? goals[goals.length - 2].pointsNeeded : 0;
  const stepPoints = maxPoints - penPoints;
  const nLast      = chests.length > 0 ? chests[chests.length - 1].timesClaimed : 0;
  const nextRepeatableThreshold = isMonthly && nLast > 0
    ? penPoints + (nLast + 1) * stepPoints
    : null;
  const displayMaxPoints = nextRepeatableThreshold ?? maxPoints;
  const localConsumedPoints = nLast >= 1
    ? penPoints + nLast * stepPoints
    : chests.reduce((max, c) => (c.opened && c.pointsNeeded > max ? c.pointsNeeded : max), 0);
  const consumedPoints = isMonthly ? (backendConsumedPoints ?? localConsumedPoints) : 0;
  const balancePoints  = playerPoints - consumedPoints;

  const nextChest    = chests.find(c => !c.opened && playerPoints < c.pointsNeeded);

  const computeProgressPct = (points) => {
    if (chests.length === 0 || points <= 0) return 0;
    if (points >= maxPoints) return 100;
    const segIdx = chests.findIndex(c => points < c.pointsNeeded);
    if (segIdx === -1) return 100;
    const prevPoints = segIdx === 0 ? 0 : goals[segIdx - 1].pointsNeeded;
    const range = chests[segIdx].pointsNeeded - prevPoints;
    const fillRatio = range > 0 ? Math.min(Math.max(points - prevPoints, 0) / range, 1) : 0;
    return (segIdx + fillRatio) / chests.length * 100;
  };
  const progressPct          = computeProgressPct(playerPoints);
  const projectedPoints      = playerPoints + pendingPoints;
  const projectedProgressPct = computeProgressPct(projectedPoints);
  const hasProjection        = pendingPoints > 0 && projectedProgressPct > progressPct;

  const barSegments = chests.map((chest, i) => {
    const prevPoints  = i === 0 ? 0 : goals[i - 1].pointsNeeded;
    const range       = chest.pointsNeeded - prevPoints;
    const fill        = range > 0 ? Math.min(Math.max(playerPoints - prevPoints, 0) / range, 1) * 100 : 0;
    return { key: chest.goalId, fill };
  });
  const pointsToNext = (() => {
    if (nextRepeatableThreshold !== null && playerPoints < nextRepeatableThreshold) {
      return nextRepeatableThreshold - playerPoints;
    }
    return nextChest ? nextChest.pointsNeeded - playerPoints : 0;
  })();

  const periodText = startDate && endDate
    ? `${formatDate(startDate)} ${lp.periodConnector} ${formatDate(endDate)}`
    : endDate ? formatDate(endDate) : '—';

  const getItemName = (itemId) => {
    const names = itemNames[itemId];
    if (!names) return '...';
    return lang === 'USD' ? (names[1] ?? names[0] ?? '') : (names[0] ?? '');
  };

  const formatItemType = (raw) => {
    if (!raw) return '';
    const key = String(raw)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/\s+/g, '');
    return (lp.itemTypes?.[key] ?? raw).toUpperCase();
  };

  const claimErrorMessage = (status) => {
    const errs = lp.claimModal.errors;
    return {
      404: errs.notFound,
      409: errs.editionClosed ?? errs.alreadyClaimed,
      403: errs.accountBlocked,
      400: errs.insufficientPoints,
    }[status] ?? errs.unknown;
  };

  const handleClaim = (chest) => {
    setClaiming(chest.goalId);
    api.post(CLAIM_API, { accountName, goalId: chest.goalId, token })
      .then(() => {
        setLocalClaimed(prev => [...prev, chest.goalId]);
        setActivePopup(null);
        setClaimModal({ type: 'success', chest });
        onClaimSuccess?.();
      })
      .catch(err => {
        const serverMessage = err.response?.data?.message;
        if (err.response?.status === 500 && !serverMessage) return;
        setClaimModal({ type: 'error', message: serverMessage ?? claimErrorMessage(err.response?.status) });
      })
      .finally(() => setClaiming(null));
  };

  const handleChestEnter = (chest, e) => {
    if (!scrollContainerRef.current || dragState.current.active) return;
    const containerRect = e.currentTarget.getBoundingClientRect();
    const scrollRect    = scrollContainerRef.current.getBoundingClientRect();
    setPopupFlipped(false);
    setActivePopup({
      chest,
      centerX: containerRect.left - scrollRect.left + containerRect.width / 2,
      bottom:  scrollRect.bottom - containerRect.top - 60,
      isLast:  isMonthly && chest.goalId === chests[chests.length - 1]?.goalId,
    });
  };

  const isMobile = typeof window !== 'undefined' && window.innerWidth < 768;
  const popupStyle = activePopup ? (isMobile ? {
    left:         (window.innerWidth / 2) - (scrollContainerRef.current?.getBoundingClientRect().left ?? 0),
    transform:    'translateX(-50%)',
    bottom:       activePopup.bottom,
    borderRadius: '8px 8px 8px 8px',
  } : {
    left:         popupFlipped ? 'auto' : activePopup.centerX,
    right:        popupFlipped ? (scrollContainerRef.current?.offsetWidth ?? 0) - activePopup.centerX : 'auto',
    bottom:       activePopup.bottom,
    borderRadius: popupFlipped ? '8px 8px 0 8px' : '8px 8px 8px 0',
  }) : {};

  return (
    <div className="journey">
      <div className="journey-name">{label}</div>
      <div className={`journey-scroll-hint${isOverflowing ? '' : ' journey-scroll-hint--placeholder'}`}>
        {isOverflowing ? lp.scrollHint : ' '}
      </div>

      <div className="journey-scroll-container" ref={scrollContainerRef}>
        {/* Popup rendered here — outside the scroll inner, so overflow-x: auto never clips it */}
        {activePopup && (
          <div ref={popupRef} className="chest-popup" style={popupStyle}>
            <div className={`chest-popup-content${activePopup.chest.items.length > 1 ? ' chest-popup-content--multi' : ''}`}>
              {activePopup.chest.items.map(item => (
                <div key={item.itemId} className="chest-popup-item-row">
                  <img
                    src={`https://wydmisc.raidhut.com.br/icons/${serverMap}/${item.itemId}.gif`}
                    alt={`Item ${item.itemId}`}
                    className="chest-popup-item-icon"
                  />
                  <div className="chest-popup-item-info">
                    <span className="chest-popup-name">
                      {item.efQuantity != null ? `[${String(item.efQuantity > 0 ? item.efQuantity : 1).padStart(2, '0')}] ` : ''}{getItemName(item.itemId)}
                    </span>
                    <span className="chest-popup-item-points">
                      {lp.requirement} {fmtN(activePopup.chest.pointsNeeded)} {lp.points}
                    </span>
                    <span className="chest-popup-item-type">
                      {formatItemType(item.itemType)}
                    </span>
                  </div>
                </div>
              ))}
              {activePopup.chest.items.length === 0 && (
                <span className="chest-popup-item-points">
                  {lp.requirement} {fmtN(activePopup.chest.pointsNeeded)} {lp.points}
                </span>
              )}
            </div>
            {activePopup.isLast && (
              <div className="chest-popup-repeatable-hint">
                <span className="chest-popup-repeatable-title">∞ {lp.repeatableTitle}</span>
                <span className="chest-popup-repeatable-desc">{lp.repeatableHint}</span>
                {stepPoints > 0 && (
                  <span className="chest-popup-repeatable-cost">
                    {lp.repeatableCost.replace('{n}', fmtN(stepPoints))}
                  </span>
                )}
                {activePopup.chest.timesClaimed > 0 && (
                  <span className="chest-popup-repeatable-claimed">
                    {lp.timesClaimedLabel.replace('{n}', activePopup.chest.timesClaimed)}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        <div className={`journey-scroll-inner${isOverflowing ? ' journey-scroll-inner--overflow' : ''}`} ref={scrollInnerRef}>
          <div className="chests-and-bar" style={{ '--chest-count': chests.length, minWidth: isOverflowing ? `${chests.length * 200}px` : '100%' }}>
            <div className="points-labels">
              {chests.map((chest, idx) => {
                const leftPct = ((idx + 1) / chests.length) * 100;
                const isLastMark = isMonthly && idx === chests.length - 1 && nLast > 0;
                return (
                  <span
                    key={chest.goalId}
                    className={`points-label${isLastMark ? ' points-label--repeatable' : ''}`}
                    style={{ left: `${leftPct}%` }}
                  >
                    {isLastMark ? (
                      <>
                        <span>{fmtN(displayMaxPoints)}</span>
                        <span className="points-label-sub">{fmtN(chest.pointsNeeded)} +({nLast}×{fmtN(stepPoints)})</span>
                      </>
                    ) : fmtN(chest.pointsNeeded)}
                  </span>
                );
              })}
            </div>

            <div
              ref={barWrapperRef}
              className="bar-wrapper"
              onMouseMove={isMobile ? undefined : (e) => {
                const rect = e.currentTarget.getBoundingClientRect();
                setBarTooltipPos({ top: rect.top - 18, left: e.clientX });
              }}
              onMouseLeave={isMobile ? undefined : () => setBarTooltipPos(null)}
            >
              <div className="bar-segments">
                {barSegments.map(seg => (
                  <div key={seg.key} className="bar-segment">
                    <div className="bar-segment-fill" style={{ width: `${100 - seg.fill}%` }} />
                  </div>
                ))}
              </div>
              {chests.map((chest, idx) => {
                const leftPct = ((idx + 1) / chests.length) * 100;
                const passed  = playerPoints >= chest.pointsNeeded;
                return (
                  <img
                    key={chest.goalId}
                    src={passed
                      ? 'img/loyalty/progress-bar/journey-progress-mark.svg'
                      : 'img/loyalty/progress-bar/journey-progress-mark-future.svg'}
                    alt=""
                    className={`bar-mark${passed ? ' bar-mark--passed' : ''}`}
                    style={{ left: `${leftPct}%` }}
                    draggable="false"
                  />
                );
              })}
              {hasProjection && (
                <div
                  className="bar-projection"
                  style={{
                    left: `${progressPct}%`,
                    width: `${Math.min(projectedProgressPct, 100) - progressPct}%`,
                  }}
                />
              )}
              <img
                src="img/loyalty/progress-bar/journey-progress-mark.svg"
                alt=""
                className="bar-mark bar-mark--current"
                style={{ left: `${progressPct}%` }}
                draggable="false"
              />
              {hasProjection && (
                <img
                  src="img/loyalty/progress-bar/journey-progress-mark.svg"
                  alt=""
                  className="bar-mark bar-mark--projected"
                  style={{ left: `${Math.min(projectedProgressPct, 100)}%` }}
                  draggable="false"
                />
              )}
              <div
                className="bar-tooltip"
                style={(() => {
                  if (isMobile) {
                    const rect = barWrapperRef.current?.getBoundingClientRect();
                    return rect
                      ? { top: rect.top - 18, left: (typeof window !== 'undefined' ? window.innerWidth / 2 : 0) }
                      : undefined;
                  }
                  return barTooltipPos ? { top: barTooltipPos.top, left: barTooltipPos.left } : undefined;
                })()}
              >
                <div className="bar-tooltip-row">
                  <img
                    src="img/loyalty/progress-bar/journey-progress-mark.svg"
                    alt=""
                    className="bar-tooltip-mark"
                    draggable="false"
                  />
                  <span>{lp.currentPointsLabel}</span>
                </div>
                <div className="bar-tooltip-row">
                  <img
                    src="img/loyalty/progress-bar/journey-progress-mark.svg"
                    alt=""
                    className="bar-tooltip-mark bar-tooltip-mark--projected"
                    draggable="false"
                  />
                  <span>{lp.projectedPointsLabel}</span>
                </div>
              </div>
            </div>

            <div className="chests-row">
              {chests.map((chest, idx) => {
                const leftPct      = ((idx + 1) / chests.length) * 100;
                const isLast       = isMonthly && idx === chests.length - 1;
                const prevsClaimed = chests.slice(0, idx).every(c => c.opened);

                let statusKey;
                if (isLast) {
                  // Última meta (mensal): resgate repetido — o (n+1)-ésimo resgate
                  // exige disponível >= penúltima + (n+1)×step
                  const required = penPoints + (chest.timesClaimed + 1) * stepPoints;
                  const canClaim = prevsClaimed && stepPoints > 0 && playerPoints >= required;
                  statusKey = canClaim ? 'claim' : chest.opened ? 'claimed' : 'blocked';
                } else if (isMonthly) {
                  // Mensal: resgate sequencial — exige as metas anteriores resgatadas
                  statusKey = chest.opened ? 'claimed'
                    : prevsClaimed && playerPoints >= chest.pointsNeeded ? 'claim'
                    : 'blocked';
                } else {
                  statusKey = chest.opened ? 'claimed'
                    : prevsClaimed && playerPoints >= chest.pointsNeeded ? 'claim'
                    : 'blocked';
                }
                const isAvailable = statusKey === 'claim';

                return (
                  <div
                    key={chest.goalId}
                    className={`chest-container${isLast ? ' chest-container--repeatable' : ''}`}
                    style={{ left: `${leftPct}%` }}
                    onMouseEnter={(e) => handleChestEnter(chest, e)}
                    onMouseLeave={() => { setActivePopup(null);}}
                    onClick={isMobile ? (e) => {
                      if (activePopup?.chest?.goalId === chest.goalId) {
                        setActivePopup(null);
                      } else {
                        handleChestEnter(chest, e);
                      }
                    } : undefined}
                  >
                    <div
                      className={`chest-status chest-status--${statusKey}${statusKey === 'claim' && isLast && chest.timesClaimed > 0 ? ' chest-status--claim-again' : ''}${statusKey === 'claim' && claiming !== null && claiming !== chest.goalId ? ' chest-status--claim-locked' : ''}`}
                      onClick={statusKey === 'claim' && claiming === null ? (e) => { e.stopPropagation(); handleClaim(chest); } : undefined}
                    >
                      <span>
                        {claiming === chest.goalId
                          ? '.'.repeat(loadingDots)
                          : statusKey === 'blocked' ? lp.blocked
                          : statusKey === 'claim'   ? (isLast && chest.timesClaimed > 0 ? lp.claimAgain : lp.claim)
                          : (isLast || chest.timesClaimed <= 1) ? lp.claimed
                          :                           lp.claimedTimes.replace('{n}', chest.timesClaimed)}
                      </span>
                    </div>

                    <div
                      className="chest-wrapper"
                      onClick={statusKey === 'claim' && claiming === null && !isMobile ? () => handleClaim(chest) : undefined}
                      style={{ cursor: statusKey === 'claim' && claiming === null ? 'pointer' : undefined }}
                    >
                      <img
                        src={chestImg(chest.phase, chest.opened, isAvailable, chest.chestType)}
                        alt={`Chest ${idx + 1}`}
                        className="chest-img"
                        draggable="false"
                      />
                    </div>

                    {isLast && (
                      <div className="chest-repeatable-tags">
                        <div className="chest-repeatable-tag">∞ {lp.repeatable}</div>
                        {chest.timesClaimed > 0 && (
                          <span className="chest-repeatable-count">
                            {lp.timesClaimedLabel.replace('{n}', chest.timesClaimed)}
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>

      <div className="journey-info-grid">
        <div className="journey-info-col">
          <span className="journey-info-label">{lp.period}</span>
          <span className="journey-info-value">{periodText}</span>
          <span className="journey-info-countdown">{lp.endsOn} {timeLeft}</span>
        </div>
        <div className="journey-info-col">
          <span className="journey-info-label">{lp.accumulatedPoints}</span>
          <span className="journey-info-value journey-info-value--points">
            <span className="journey-info-current-points">{fmtN(playerPoints)}</span>
            {' / '}{fmtN(displayMaxPoints)} pts
          </span>
          {pointsToNext > 0 && (
            <span className="journey-info-countdown">
              {lp.faltam} {fmtN(pointsToNext)} {lp.missingPoints}
            </span>
          )}
          {isMonthly && consumedPoints > 0 && (
            <span className="journey-info-balance">
              {lp.balance}: {fmtN(balancePoints)} pts · {lp.consumed}: {fmtN(consumedPoints)} pts
            </span>
          )}
          {isMonthly && carryoverPoints > 0 && (
            <span className="journey-info-countdown">
              {lp.carryoverReceived}: {fmtN(carryoverPoints)} pts
            </span>
          )}
          {isMonthly && (
            <span className="journey-info-countdown">
              {carryoverPct >= 100
                ? lp.carryoverNextFull
                : lp.carryoverNextPartial.replace('{pct}', carryoverPct)}
            </span>
          )}
        </div>
      </div>

      {claimModal && (
        <div className="claim-modal-overlay" onClick={() => setClaimModal(null)}>
          <div className="claim-modal" onClick={e => e.stopPropagation()}>
            {claimModal.type === 'success' ? (
              <>
                <span className="claim-modal-title">{lp.claimModal.successTitle}</span>
                <div className={`chest-popup-content${claimModal.chest.items.length > 1 ? ' chest-popup-content--multi' : ''}`}>
                  {claimModal.chest.items.map(item => (
                    <div key={item.itemId} className="chest-popup-item-row">
                      <img
                        src={`https://wydmisc.raidhut.com.br/icons/${serverMap}/${item.itemId}.gif`}
                        alt={`Item ${item.itemId}`}
                        className="chest-popup-item-icon"
                      />
                      <div className="chest-popup-item-info">
                        <span className="chest-popup-name">
                          {item.efQuantity != null ? `[${String(item.efQuantity > 0 ? item.efQuantity : 1).padStart(2, '0')}] ` : ''}{getItemName(item.itemId)}
                        </span>
                        <span className="chest-popup-item-type">{formatItemType(item.itemType)}</span>
                      </div>
                    </div>
                  ))}
                </div>
                <span className="claim-modal-subtitle">
                  {claimModal.chest.items.length === 1
                    ? lp.claimModal.successSingle.replace('{item}', getItemName(claimModal.chest.items[0].itemId))
                    : lp.claimModal.successMulti}
                </span>
              </>
            ) : (
              <>
                <span className="claim-modal-title claim-modal-title--error">{lp.claimModal.errorTitle}</span>
                <span className="claim-modal-message">{claimModal.message}</span>
              </>
            )}
            <button className="claim-modal-close" onClick={() => setClaimModal(null)}>{lp.claimModal.close}</button>
          </div>
        </div>
      )}
    </div>
  );
}
